import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { cn } from '@goaltree/ui/lib/utils'
import { useQuery } from 'convex/react'
import { useId } from 'react'
import { useTranslation } from 'react-i18next'

import { useGrowIn } from '@/hooks/use-grow-in'

type Trend = 'rising' | 'falling' | 'steady'

/** The habit's strength from the (already subscribed) habits list. */
export function useHabitStrength(habitId: string | undefined) {
  const habits = useQuery(api.nodes.getHabits, habitId ? {} : 'skip')
  const h = habitId ? habits?.find((x) => x._id === habitId) : undefined
  return h ? { strength: h.strength, ifMissed: h.strengthIfMissed } : null
}

export function StrengthRing({
  value,
  size = 11,
  stroke = 2.25,
  color,
  className,
}: {
  value: number
  size?: number
  stroke?: number
  // CSS colour for the arc; the primary colour by default.
  color?: string
  className?: string
}) {
  const r = size / 2 - stroke / 2
  const c = 2 * Math.PI * r
  const grown = useGrowIn()
  const pct = grown ? Math.max(0, Math.min(100, value)) / 100 : 0
  return (
    <svg
      aria-hidden
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={cn('shrink-0 -rotate-90', className)}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        strokeWidth={stroke}
        className="stroke-foreground/[0.1]"
      />
      {value > 0 && (
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${c * pct} ${c}`}
          className={cn(
            !color && 'stroke-primary',
            'motion-safe:transition-[stroke-dasharray] motion-safe:duration-700 motion-safe:ease-out',
          )}
          style={color ? { stroke: color } : undefined}
        />
      )}
    </svg>
  )
}

/** Compact "◔ 82%" for a habit row's meta line. */
export function StrengthChip({ value, className }: { value: number; className?: string }) {
  const { t } = useTranslation()
  const label = t('strength.value', { pct: value })
  return (
    <span
      title={label}
      aria-label={label}
      className={cn('inline-flex items-center gap-[3px] align-[-0.1em]', className)}
    >
      <StrengthRing value={value} />
      {value}%
    </span>
  )
}

function Sparkline({ series, color }: { series: number[]; color?: string }) {
  const gid = useId()
  const w = 100
  const h = 32
  if (series.length < 2) return null
  // Right-aligned on a 90-day axis so a young habit's line starts part way in.
  const span = Math.max(series.length, 90) - 1
  const x0 = ((span - (series.length - 1)) / span) * w
  const pts = series.map((v, i) => [x0 + (i / span) * w, h - 1 - (v / 100) * (h - 2)] as const)
  const line = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(2)},${y.toFixed(2)}`).join('')
  const area = `${line}L${w},${h}L${x0.toFixed(2)},${h}Z`
  const stroke = color ?? 'var(--primary)'
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className="h-10 w-full overflow-visible"
      aria-hidden
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity={0.22} />
          <stop offset="100%" stopColor={stroke} stopOpacity={0} />
        </linearGradient>
      </defs>
      {[25, 50, 75].map((g) => (
        <line
          key={g}
          x1={0}
          x2={w}
          y1={h - 1 - (g / 100) * (h - 2)}
          y2={h - 1 - (g / 100) * (h - 2)}
          className="stroke-foreground/[0.06]"
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
      ))}
      <path d={area} fill={`url(#${gid})`} />
      <path
        d={line}
        fill="none"
        stroke={stroke}
        strokeWidth={1.75}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}

/** Ring + trend + 90-day line, for the habit sheet and stats page. */
export function StrengthPanel({
  strength,
  trend,
  series,
  color,
  className,
}: {
  strength: number
  trend: Trend
  series: number[]
  color?: string
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <div className={cn('flex items-center gap-3', className)}>
      <div className="relative shrink-0">
        <StrengthRing value={strength} size={48} stroke={5} color={color} />
        <span className="absolute inset-0 flex items-center justify-center font-mono text-[12px] font-bold tabular-nums text-foreground">
          {strength}%
        </span>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="flex items-baseline justify-between gap-2 text-xs">
          <span className="font-semibold text-foreground">
            {t('strength.label')}
            <span
              className={cn(
                'ml-1.5 font-mono text-[10.5px] font-medium',
                trend === 'rising'
                  ? 'text-lime-700 dark:text-lime-400'
                  : trend === 'falling'
                    ? 'text-amber-700 dark:text-amber-400'
                    : 'text-muted-foreground',
              )}
            >
              {trend === 'rising' ? '↗ ' : trend === 'falling' ? '↘ ' : ''}
              {t(`strength.trend_${trend}`)}
            </span>
          </span>
          <span className="shrink-0 font-mono text-[10.5px] text-muted-foreground">
            {t('strength.days90')}
          </span>
        </p>
        <Sparkline series={series} color={color} />
        <p className="text-[11px] text-muted-foreground">{t('strength.hint')}</p>
      </div>
    </div>
  )
}

/** Self-loading panel for the habit detail sheet. */
export function HabitStrengthPanel({
  habitId,
  color,
  className,
}: {
  habitId: Id<'nodes'>
  color?: string
  className?: string
}) {
  const stats = useQuery(api.habitStats.getHabitStats, { habitId })
  if (!stats) return null
  return (
    <StrengthPanel
      strength={stats.strength}
      trend={stats.strengthTrend}
      series={stats.strengthSeries}
      color={color}
      className={className}
    />
  )
}
